import { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { Table } from '@tanstack/react-table';
import { Virtualizer } from '@tanstack/react-virtual';
import styles from '../bmweDataTable.module.css';
import { LoaderRow } from './rows/loaderRow';
import { SkeletonRow } from './rows/skeletonRow';
import { DataRow } from './rows/dataRow';
import { EmptyStateRow } from './rows/emptyStateRow';

interface VirtualizedTableBodyProps<TRow extends Record<string, unknown>> {
  table: Table<TRow>;
  rowsData: TRow[];
  rowVirtualizer: Virtualizer<HTMLElement, Element>;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
  loadingSkeletonRow?: ReactNode;
  getRowAriaLabel?: (row: TRow) => string;
  selectedRowIds?: ReadonlySet<string>;
  onRowSelect?: (rowId: string) => void;
  columnsCount: number;
  noData?: boolean;
  emptyState?: ReactNode;
}

export function VirtualizedTableBody<TRow extends Record<string, unknown>>({
  table,
  rowsData,
  rowVirtualizer,
  hasMore,
  isFetchingNextPage,
  fetchNextPage,
  loadingSkeletonRow,
  getRowAriaLabel,
  selectedRowIds,
  onRowSelect,
  columnsCount,
  noData,
  emptyState,
}: VirtualizedTableBodyProps<TRow>) {
  const pendingFetchRef = useRef(false);
  const visibleColumns = table.getVisibleLeafColumns();

  useEffect(() => {
    const virtualItems = rowVirtualizer.getVirtualItems();
    const hasLoaderRow = virtualItems.some(
      (item) => hasMore && item.index === rowsData.length,
    );

    if (
      hasLoaderRow &&
      hasMore &&
      !isFetchingNextPage &&
      !pendingFetchRef.current
    ) {
      pendingFetchRef.current = true;
      void fetchNextPage().finally(() => {
        pendingFetchRef.current = false;
      });
    }
  }, [
    rowVirtualizer,
    hasMore,
    rowsData.length,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  if (noData) {
    return (
      <tbody className={styles.bmweDataTableBody}>
        <EmptyStateRow columnsCount={columnsCount} emptyState={emptyState} />
      </tbody>
    );
  }

  return (
    <tbody
      className={styles.bmweDataTableBody}
      style={
        {
          '--bodyHeight': `${rowVirtualizer.getTotalSize()}px`,
        } as CSSProperties
      }
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const isLoaderRow = hasMore && virtualRow.index === rowsData.length;
        const realRow = !isLoaderRow
          ? table.getRowModel().rows[virtualRow.index]
          : undefined;
        const isLoaded = !!realRow;

        const ariaRowIndex = virtualRow.index + 2; // +1 header +1 1-based

        if (isLoaderRow) {
          return (
            <LoaderRow
              key="loader-row"
              columnsCount={columnsCount}
              virtualStart={virtualRow.start}
              virtualIndex={virtualRow.index}
              ariaRowIndex={ariaRowIndex}
              measure={(el) =>
                rowVirtualizer.measureElement(el as Element | null)
              }
            />
          );
        }

        if (!isLoaded) {
          return (
            <SkeletonRow
              key={`skeleton-${virtualRow.index}`}
              columns={visibleColumns}
              virtualStart={virtualRow.start}
              virtualIndex={virtualRow.index}
              ariaRowIndex={ariaRowIndex}
              measure={(el) =>
                rowVirtualizer.measureElement(el as Element | null)
              }
              loadingSkeletonRow={loadingSkeletonRow}
            />
          );
        }

        if (!realRow) {
          return null; // safeguard
        }

        return (
          <DataRow
            key={realRow.id}
            row={realRow}
            virtualStart={virtualRow.start}
            virtualIndex={virtualRow.index}
            ariaRowIndex={ariaRowIndex}
            measure={(el) =>
              rowVirtualizer.measureElement(el as Element | null)
            }
            getRowAriaLabel={getRowAriaLabel}
            isSelected={selectedRowIds?.has(realRow.id)}
            onSelect={onRowSelect}
          />
        );
      })}
    </tbody>
  );
}
